import { NgClass } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';

import { MaskitoDirective } from '@maskito/angular';
import { TranslateModule } from '@ngx-translate/core';
import { finalize } from 'rxjs';

import { Account } from '@personal-area/global/accounts/models';
import { AccountService } from '@personal-area/global/accounts/services';
import { TypeConverter } from '@personal-area/global/common';
import { ErrorTexts } from '@personal-area/global/form-validation/models';
import { FormFieldComponent, Mask } from '@personal-area/global/ui';

const AMOUNT_MIN_VAL = 1; // USD
const AMOUNT_MAX_VAL = 1000000 * 100; // cents

@Component({
    selector: 'add-demo-funds-widget',
    templateUrl: './add-demo-funds-widget.component.html',
    styleUrls: ['./add-demo-funds-widget.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
    NgClass,
    ReactiveFormsModule,
    FormFieldComponent,
    MaskitoDirective,
    TranslateModule
]
})
export class AddDemoFundsWidgetComponent implements OnInit {
  @Input() account: Account;
  @Input() isChild: boolean;
  @Output() error: EventEmitter<HttpErrorResponse>;
  @Output() success: EventEmitter<number>;

  form: UntypedFormGroup;
  isLoading: boolean;
  submitErrors: EventEmitter<void>;
  resetForm: EventEmitter<void>;
  readonly maskOptions = Mask.getDecimalPlacesMask();

  constructor(
    private _cdRef: ChangeDetectorRef,
    private _formBuilder: UntypedFormBuilder,
    private _accountService: AccountService,
  ) {
    this.error = new EventEmitter();
    this.success = new EventEmitter();
    this.submitErrors = new EventEmitter<void>();
    this.resetForm = new EventEmitter<void>();
  }

  ngOnInit() {
    this.form = this._formBuilder.group({
      amount: ['', [
        Validators.required,
        Validators.pattern(/^[0-9.,]+$/),
        Validators.min(AMOUNT_MIN_VAL),
        this._maxAmountValidator(),
      ]],
    });
  }

  public fieldErrors(field: string): ErrorTexts {
    return {
      required: `account.error.${field}-required`,
      pattern: `account.error.${field}-pattern`,
      max: `account.error.${field}-max`,
      min: `account.error.${field}-min`,
    };
  }

  public addFunds() {
    if (!this.form.valid) {
      this.submitErrors.next();
      return;
    }
    if (this.isLoading) {
      return;
    }

    const val = this.form.value;

    this.isLoading = true;
    this._accountService.addDemoFunds(this.account.userId, this.account.id, TypeConverter.moneyToNumber(val.amount))
      .pipe(
        finalize(() => {
          this.isLoading = false;
          this._cdRef.detectChanges();
        }),
      )
      .subscribe(
        () => {
          this.success.emit(val.amount * 100);
          this.resetForm.emit();
          this.form.reset();
        },
        err => this.error.emit(err),
      );
  }

  private _maxAmountValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      return (TypeConverter.moneyToNumber(control.value) + this.account.balance > AMOUNT_MAX_VAL)
        ? { 'max': true } :
        null;
    };
  }
}
